// Browser-compatible database service using a REST API proxy
// This service provides a browser-compatible way to interact with a MySQL database

let isInitializing = false;
let initPromise: Promise<boolean> | null = null;
let connectionAttempts = 0;
const MAX_RETRY_ATTEMPTS = 3;
const RETRY_DELAY_BASE = 1000; // Base delay in milliseconds

// Database proxy service URL - use the same host as the application
// This ensures we're always connecting to the same host where the secrets are configured
const DB_PROXY_URL = typeof window !== 'undefined' ? window.location.origin : '';

// Function to initialize the database connection
const initializeDatabase = async (): Promise<boolean> => {
  if (initPromise) {
    return initPromise;
  }

  if (isInitializing) {
    // Wait for initialization to complete
    return new Promise<boolean>(resolve => {
      const checkInterval = setInterval(() => {
        if (!isInitializing) {
          clearInterval(checkInterval);
          resolve(connectionAttempts > 0);
        }
      }, 100);
    });
  }

  isInitializing = true;
  connectionAttempts = 0;
  initPromise = (async () => {
    try {
      // Always use SSL for database connections
      return await attemptConnection();
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      console.error(`Error during database initialization: ${errorMsg}`);
      isInitializing = false;
      initPromise = null; // Reset promise so it can be retried
      return false;
    }
  })();

  return initPromise;
};

// Helper function to attempt database connection with retries
const attemptConnection = async (): Promise<boolean> => {
  while (connectionAttempts < MAX_RETRY_ATTEMPTS) {
    try {
      connectionAttempts++;

      // Test the connection by making a simple query
      const testResponse = await fetch(`${DB_PROXY_URL}/test-connection`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ssl: {
            rejectUnauthorized: true // Always require SSL and verify server certificate
          }
        })
      });

      if (!testResponse.ok) {
        const errorData = await testResponse.json();
        throw new Error(`Database connection test failed: ${errorData.error || 'Unknown error'}`);
      }

      isInitializing = false;
      return true;
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      console.error(`Connection attempt ${connectionAttempts}/${MAX_RETRY_ATTEMPTS} failed: ${errorMsg}`);

      // If we've reached max attempts, give up
      if (connectionAttempts >= MAX_RETRY_ATTEMPTS) {
        console.error('Maximum connection attempts reached. Giving up.');
        break;
      }

      // Calculate exponential backoff delay
      const delay = RETRY_DELAY_BASE * Math.pow(2, connectionAttempts - 1);

      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  isInitializing = false;
  initPromise = null;
  return false;
};

// Function to execute a query
export const query = async <T>(sql: string, params?: any[]): Promise<T> => {
  // Ensure database is initialized
  const initialized = await initializeDatabase();

  if (!initialized) {
    const error = new Error('Database connection is not available');
    console.error(error);
    throw error;
  }

  try {
    // Execute query through the proxy service
    const response = await fetch(`${DB_PROXY_URL}/query`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        sql,
        params,
        // Always require SSL for database connections
        ssl: {
          rejectUnauthorized: true // Require SSL and verify server certificate
        }
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`Database query failed: ${errorData.error || 'Unknown error'}`);
    }

    const result = await response.json();
    return result.data as T;
  } catch (error) {
    console.error('Database query error:', error);
    throw error;
  }
};

// Function to execute a transaction
export const transaction = async <T>(callback: (connection: any) => Promise<T>): Promise<T> => {
  // Ensure database is initialized
  const initialized = await initializeDatabase();

  if (!initialized) {
    const error = new Error('Database connection is not available');
    console.error(error);
    throw error;
  }

  // Create a transaction proxy object that will send all queries to the proxy service
  // with the same transaction ID
  const transactionId = Math.random().toString(36).substring(2, 15);

  try {
    // Start transaction
    const startResponse = await fetch(`${DB_PROXY_URL}/transaction/begin`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        transactionId,
        // Always require SSL for database connections
        ssl: {
          rejectUnauthorized: true // Require SSL and verify server certificate
        }
      })
    });

    if (!startResponse.ok) {
      const errorData = await startResponse.json();
      throw new Error(`Failed to start transaction: ${errorData.error || 'Unknown error'}`);
    }

    // Create a proxy connection object that will be passed to the callback
    const connectionProxy = {
      execute: async (sql: string, params?: any[]) => {
        const response = await fetch(`${DB_PROXY_URL}/transaction/query`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            transactionId,
            sql,
            params,
            // Always require SSL for database connections
            ssl: {
              rejectUnauthorized: true // Require SSL and verify server certificate
            }
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(`Transaction query failed: ${errorData.error || 'Unknown error'}`);
        }

        const result = await response.json();
        return [result.data];
      },
      query: async (sql: string, params?: any[]) => {
        return connectionProxy.execute(sql, params);
      }
    };

    // Execute the callback with our proxy connection
    const result = await callback(connectionProxy);

    // Commit the transaction
    const commitResponse = await fetch(`${DB_PROXY_URL}/transaction/commit`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        transactionId,
        // Always require SSL for database connections
        ssl: {
          rejectUnauthorized: true // Require SSL and verify server certificate
        }
      })
    });

    if (!commitResponse.ok) {
      const errorData = await commitResponse.json();
      throw new Error(`Failed to commit transaction: ${errorData.error || 'Unknown error'}`);
    }

    return result;
  } catch (error) {
    // Rollback the transaction on error
    try {
      await fetch(`${DB_PROXY_URL}/transaction/rollback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          transactionId,
          // Always require SSL for database connections
          ssl: {
            rejectUnauthorized: true // Require SSL and verify server certificate
          }
        })
      });
    } catch (rollbackError) {
      console.error('Error rolling back transaction:', rollbackError);
    }

    console.error('Transaction error:', error);
    throw error;
  }
};

// Test database connection
export const testConnection = async (): Promise<boolean> => {
  try {
    // Ensure database is initialized
    const initialized = await initializeDatabase();

    if (!initialized) {
      const error = new Error('Database connection is not available');
      console.error(error);
      return false;
    }

    // Test basic connectivity
    const testResponse = await fetch(`${DB_PROXY_URL}/test-connection`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        // Always require SSL for database connections
        ssl: {
          rejectUnauthorized: true // Require SSL and verify server certificate
        }
      })
    });

    if (!testResponse.ok) {
      const errorData = await testResponse.json();
      throw new Error(`Database connection test failed: ${errorData.error || 'Unknown error'}`);
    }

    // Test table access
    try {
      const tableAccessResponse = await fetch(`${DB_PROXY_URL}/query`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sql: 'SHOW TABLES',
          params: [],
          // Always require SSL for database connections
          ssl: {
            rejectUnauthorized: true // Require SSL and verify server certificate
          }
        })
      });

      if (!tableAccessResponse.ok) {
        const errorData = await tableAccessResponse.json();
        console.warn('Database connected but may have permission issues:', errorData.error || 'Unknown error');
      }
    } catch (permissionError) {
      console.warn('Database connected but may have permission issues:', 
        permissionError instanceof Error ? permissionError.message : String(permissionError));
      // Still return true since basic connection works
    }

    return true;
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : String(error);
    console.error('Database connection test failed:', errorMsg);

    // Provide more specific error information based on error message
    if (errorMsg.includes('ECONNREFUSED') || errorMsg.includes('Failed to fetch')) {
      console.error(`Cannot connect to database server or the proxy service is unavailable.`);
    } else if (errorMsg.includes('ER_ACCESS_DENIED_ERROR')) {
      console.error(`Access denied for database user.`);
    } else if (errorMsg.includes('ER_BAD_DB_ERROR')) {
      console.error(`Database does not exist.`);
    } else if (errorMsg.includes('ETIMEDOUT')) {
      console.error(`Connection timed out. Check network connectivity and firewall settings.`);
    }

    return false;
  }
};

// Diagnostic function to check database connectivity and configuration
export const getDatabaseDiagnostics = async (): Promise<{
  configPresent: boolean;
  connectionSuccessful: boolean;
  tableAccessSuccessful: boolean;
  errorDetails: string | null;
  configDetails: {
    configured: boolean;
  }
}> => {
  const result = {
    configPresent: false,
    connectionSuccessful: false,
    tableAccessSuccessful: false,
    errorDetails: null,
    configDetails: {
      configured: false
    }
  };

  try {
    // Check if configuration is present on the server by testing the connection
    try {
      // Always use SSL for database connections
      const testResponse = await fetch(`${DB_PROXY_URL}/test-connection`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ssl: {
            rejectUnauthorized: true // Always require SSL and verify server certificate
          }
        })
      });

      if (!testResponse.ok) {
        const errorData = await testResponse.json();
        throw new Error(`Database connection test failed: ${errorData.error || 'Unknown error'}`);
      }

      result.configPresent = true;
      result.connectionSuccessful = true;
      result.configDetails.configured = true;

      // Test table access
      try {
        const tableAccessResponse = await fetch(`${DB_PROXY_URL}/query`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            sql: 'SHOW TABLES',
            params: [],
            ssl: {
              rejectUnauthorized: true // Always require SSL and verify server certificate
            }
          })
        });

        if (!tableAccessResponse.ok) {
          const errorData = await tableAccessResponse.json();
          result.errorDetails = `Connected to database but cannot access tables: ${errorData.error || 'Unknown error'}`;
        } else {
          result.tableAccessSuccessful = true;
        }
      } catch (tableError) {
        const errorMsg = tableError instanceof Error ? tableError.message : String(tableError);
        result.errorDetails = `Connected to database but cannot access tables: ${errorMsg}`;
      }
    } catch (connError) {
      const errorMsg = connError instanceof Error ? connError.message : String(connError);
      result.errorDetails = `Connection failed: ${errorMsg}`;

      // Provide more specific error information
      if (errorMsg.includes('ECONNREFUSED') || errorMsg.includes('Failed to fetch')) {
        result.errorDetails += ` Cannot connect to database server or the proxy service is unavailable.`;
      } else if (errorMsg.includes('ER_ACCESS_DENIED_ERROR')) {
        result.errorDetails += ` Access denied for database user.`;
      } else if (errorMsg.includes('ER_BAD_DB_ERROR')) {
        result.errorDetails += ` Database does not exist.`;
      } else if (errorMsg.includes('ETIMEDOUT')) {
        result.errorDetails += ` Connection timed out. Check network connectivity and firewall settings.`;
      }
    }
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : String(error);

    // Provide helpful error information without exposing sensitive details
    if (errorMsg.includes('Failed to fetch') || errorMsg.includes('NetworkError')) {
      result.errorDetails = `Diagnostic error: ${errorMsg}`;
    } else {
      result.errorDetails = `Diagnostic error: ${errorMsg}`;
    }
  }

  return result;
};
